import React, { useState, useEffect, ChangeEvent, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    CardMedia,
    Input,
    TextField,
    Button,
    MenuItem,
    Select,
    SelectChangeEvent,
    Box,
    CircularProgress,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { useGetSubAdmin, useCreateSubAdmin, useUpdateSubAdmin } from "../../api/hooks/useSubAdmin";
import { useGetRoles } from "../../api/hooks/usePermission";
import { showSuccess, showError } from "../../helpers/toast";
import { uploadService } from "../../api/services/uploadService";
import type { CreateSubAdminPayload, UpdateSubAdminPayload } from "../../api/types/subAdmin.types";
import type { Role } from "../../api/types/permission.types";

const AddSubadmin = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const subAdminId = location?.state?.subAdminId;
    const isEditMode = !!subAdminId;

    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [countryCode, setCountryCode] = useState("+1");
    const [permission, setPermission] = useState<string>("");
    const [profile, setProfile] = useState<string>("");
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string>("");
    const [uploadingImage, setUploadingImage] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Fetch sub-admin data for edit mode
    const { data: subAdminData, isLoading: isLoadingSubAdmin, error: subAdminError } = useGetSubAdmin(
        subAdminId || '',
        { enabled: isEditMode }
    );

    // Fetch roles for dropdown
    const { data: rolesData } = useGetRoles({ limit: 100 }, { enabled: true });
    const roles: Role[] = useMemo(() => {
        const data = rolesData?.data;
        if (Array.isArray(data)) {
            return data;
        } else if (data && typeof data === 'object') {
            const dataObj = data as any;
            if ('list' in dataObj && Array.isArray(dataObj.list)) {
                return dataObj.list;
            } else if ('data' in dataObj && Array.isArray(dataObj.data)) {
                return dataObj.data;
            }
        }
        return [];
    }, [rolesData]);

    // Update form fields when sub-admin data is loaded
    useEffect(() => {
        if (subAdminData?.data && isEditMode) {
            const subAdmin = subAdminData.data;
            setFullName(subAdmin.fullName || "");
            setEmail(subAdmin.email || "");
            setPhone(subAdmin.phone || "");
            setCountryCode(subAdmin.countryCode || "+1");
            
            // Handle permissionId - can be object or string
            let permissionId = "";
            const permissionIdValue = subAdmin.permissionId as any;
            if (permissionIdValue) {
                if (typeof permissionIdValue === 'object' && permissionIdValue._id) {
                    permissionId = permissionIdValue._id;
                } else if (typeof permissionIdValue === 'string') {
                    permissionId = permissionIdValue;
                }
            }
            
            // Fallback to permission if permissionId is not available
            if (!permissionId && subAdmin.permission) {
                const permissionValue = subAdmin.permission as any;
                if (typeof permissionValue === 'object' && permissionValue._id) {
                    permissionId = permissionValue._id;
                } else if (typeof permissionValue === 'string') {
                    permissionId = permissionValue;
                }
            }
            
            setPermission(permissionId);
            setProfile(subAdmin.image || subAdmin.profile || "");
            setImagePreview(subAdmin.image || subAdmin.profile || "");
        }
    }, [subAdminData, isEditMode]);

    // Handle error
    useEffect(() => {
        if (subAdminError) {
            showError((subAdminError as any)?.response?.data?.message || "Failed to load sub-admin details");
        }
    }, [subAdminError]);

    // Create sub-admin mutation
    const { mutate: createSubAdmin, isPending: isCreating } = useCreateSubAdmin({
        onSuccess: (response) => {
            showSuccess(response.message || "Sub-admin created successfully");
            navigate("/manage-subadmin");
        },
        onError: (error: any) => {
            showError(error?.response?.data?.message || "Failed to create sub-admin");
        },
    });

    // Update sub-admin mutation
    const { mutate: updateSubAdmin, isPending: isUpdating } = useUpdateSubAdmin({
        onSuccess: (response) => {
            showSuccess(response.message || "Sub-admin updated successfully");
            navigate("/manage-subadmin");
        },
        onError: (error: any) => {
            showError(error?.response?.data?.message || "Failed to update sub-admin");
        },
    });

    const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
        const files = (event.target as HTMLInputElement).files;
        if (files && files[0]) {
            if (files[0].type.includes("image")) {
                setImageFile(files[0]);
                setImagePreview(URL.createObjectURL(files[0]));
                if (errors.profile) {
                    setErrors({ ...errors, profile: "" });
                }
            } else {
                showError("This field only accepts images.");
            }
        }
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreview("");
        setProfile("");
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!fullName.trim()) {
            newErrors.fullName = "Full name is required";
        }

        if (!email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = "Please enter a valid email address";
        }

        if (!phone.trim()) {
            newErrors.phone = "Phone number is required";
        } else {
            const phoneDigits = phone.replace(/\D/g, ''); // Remove all non-digit characters
            if (phoneDigits.length < 8) {
                newErrors.phone = "Phone number must be at least 8 digits";
            } else if (phoneDigits.length > 13) {
                newErrors.phone = "Phone number cannot exceed 13 digits";
            }
        }

        if (!permission) {
            newErrors.permission = "Role is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        // Additional validation for phone number before saving
        const phoneDigits = phone.replace(/\D/g, '');
        if (phoneDigits.length < 8 || phoneDigits.length > 13) {
            showError("Phone number must be between 8 and 13 digits");
            setErrors({ ...errors, phone: phoneDigits.length < 8 ? "Phone number must be at least 8 digits" : "Phone number cannot exceed 13 digits" });
            return;
        }

        let profileUrl = profile;

        // Upload image if a new file is selected
        if (imageFile) {
            try {
                setUploadingImage(true);
                const formData = new FormData();
                formData.append('file', imageFile);
                const uploadResponse = await uploadService.uploadFile(formData);
                if (uploadResponse?.data && typeof uploadResponse.data === 'string') {
                    profileUrl = uploadResponse.data;
                }
            } catch (error: any) {
                showError(error?.response?.data?.message || "Failed to upload image");
                setUploadingImage(false);
                return;
            } finally {
                setUploadingImage(false);
            }
        }

        if (isEditMode) {
            const payload: UpdateSubAdminPayload = {
                fullName: fullName.trim(),
                email: email.trim(),
                phone: phone.trim(),
                countryCode: countryCode,
                permissionId: permission,
            };

            if (profileUrl) {
                payload.image = profileUrl;
            }

            updateSubAdmin({ id: subAdminId, data: payload });
        } else {
            const payload: CreateSubAdminPayload = {
                fullName: fullName.trim(),
                email: email.trim(),
                phone: phone.trim(),
                countryCode: countryCode,
                permissionId: permission,
            };

            if (profileUrl) {
                payload.image = profileUrl;
            }

            createSubAdmin(payload);
        }
    };

    if (isEditMode && isLoadingSubAdmin) {
        return (
            <>
                <div className="main_title">
                    <h1>Edit Sub-Admin</h1>
                    <p>
                        <button onClick={() => navigate("/dashboard")}>Dashboard</button>
                        {' '} - <button onClick={() => navigate("/manage-subadmin")}>Manage Sub-Admin</button>
                        {' '} - Edit Sub-Admin
                    </p>
                </div>
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                    <CircularProgress />
                </Box>
            </>
        );
    }

    const isLoading = isCreating || isUpdating || uploadingImage;

    return (
        <>
            <div className="main_title">
                <h1>{isEditMode ? "Edit" : "Add"} Sub-Admin</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button>
                    {' '} - <button onClick={() => navigate("/manage-subadmin")}>Manage Sub-Admin</button>
                    {' '} - {isEditMode ? "Edit" : "Add"} Sub-Admin
                </p>
            </div>

            <div className="cards">
                <div className="form">
                    <form onSubmit={handleSubmit}>
                        <div className="gap_p">
                            <div className="control_group w_100">
                                <label htmlFor="icon-button-file">Upload Image</label>
                                {imagePreview ? (
                                    <div className="upload_image">
                                        <div className="upload_image_holder">
                                            <figure>
                                                <CardMedia component="img" image={imagePreview} alt="photo" />
                                            </figure>
                                            <CloseIcon onClick={handleRemoveImage} />
                                        </div>
                                    </div>
                                ) : (
                                    <label className="upload_image" htmlFor="icon-button-file">
                                        <Input
                                            sx={{ display: "none" }}
                                            id="icon-button-file"
                                            type="file"
                                            inputProps={{
                                                accept: "image/png,image/jpeg",
                                            }}
                                            onChange={handleImageChange}
                                            disabled={isLoading}
                                        />
                                        <span className="upload_image_holder">
                                            <figure className="add_img">
                                                <img src="/static/images/user_placeholder.png" alt="icon" />
                                                <span className="upload_img_icon">
                                                    <img src="/static/images/edit2_icon.svg" alt="icon" />
                                                </span>
                                            </figure>
                                        </span>
                                    </label>
                                )}
                            </div>

                            <div className="control_group w_33">
                                <label htmlFor="name">Name <span className="req">*</span></label>
                                <TextField
                                    id="name"
                                    placeholder="Enter Name"
                                    hiddenLabel
                                    fullWidth
                                    value={fullName}
                                    onChange={(e) => {
                                        setFullName(e.target.value);
                                        if (errors.fullName) {
                                            setErrors({ ...errors, fullName: "" });
                                        }
                                    }}
                                    error={!!errors.fullName}
                                    helperText={errors.fullName}
                                    required
                                    disabled={isLoading}
                                />
                            </div>

                            <div className="control_group w_33">
                                <label htmlFor="email">Email Id <span className="req">*</span></label>
                                <TextField
                                    id="email"
                                    type="email"
                                    placeholder="Enter Email Id"
                                    hiddenLabel
                                    fullWidth
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (errors.email) {
                                            setErrors({ ...errors, email: "" });
                                        }
                                    }}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    required
                                    disabled={isLoading}
                                />
                            </div>

                            <div className="control_group w_33">
                                <label htmlFor="phone">Phone Number <span className="req">*</span></label>
                                <PhoneInput
                                    inputProps={{ id: "phone" }}
                                    country={"us"}
                                    placeholder="Enter Phone Number"
                                    enableSearch={false}
                                    autoFormat={false}
                                    inputStyle={{ width: "100%" }}
                                    value={phone ? `${countryCode.replace('+', '')}${phone}` : countryCode.replace('+', '')}
                                    onChange={(value, data: any) => {
                                        // PhoneInput returns the full number without "+" sign (e.g., "919876543210")
                                        // Extract the phone number by removing the dial code
                                        let phoneNumber = "";
                                        if (data?.dialCode) {
                                            const dialCode = String(data.dialCode);
                                            setCountryCode(`+${data.dialCode}`);
                                            // Remove dial code from value to get just the phone number
                                            phoneNumber = value.startsWith(dialCode) 
                                                ? value.substring(dialCode.length) 
                                                : value;
                                        } else {
                                            // Fallback: remove country code prefix
                                            const dialCode = countryCode.replace('+', '');
                                            phoneNumber = value.startsWith(dialCode) 
                                                ? value.substring(dialCode.length) 
                                                : value;
                                        }
                                        
                                        // Validate phone number length (8-13 digits)
                                        const phoneDigits = phoneNumber.replace(/\D/g, '');
                                        if (phoneDigits.length <= 13) {
                                            setPhone(phoneNumber);
                                            // Clear error when valid
                                            if (errors.phone) {
                                                setErrors({ ...errors, phone: "" });
                                            }
                                        }
                                        // If more than 13 digits, don't update (prevent typing)
                                    }}
                                    disabled={isLoading}
                                />
                                {errors.phone && <h6 className="err_msg">{errors.phone}</h6>}
                            </div>

                            <div className="control_group w_33">
                                <label htmlFor="role">Role <span className="req">*</span></label>
                                <Select
                                    id="role"
                                    fullWidth
                                    value={permission}
                                    onChange={(e: SelectChangeEvent) => {
                                        setPermission(e.target.value);
                                        if (errors.permission) {
                                            setErrors({ ...errors, permission: "" });
                                        }
                                    }}
                                    displayEmpty
                                    error={!!errors.permission}
                                    disabled={isLoading}
                                >
                                    <MenuItem disabled value="">
                                        Select Role
                                    </MenuItem>
                                    {roles.map((role) => (
                                        <MenuItem key={role._id} value={role._id}>
                                            {role.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.permission && <h6 className="err_msg">{errors.permission}</h6>}
                            </div>

                        </div>

                        <div className="form_btn">
                            <Button
                                className="btn btn_primary"
                                type="submit"
                                disabled={isLoading}
                            >
                                {uploadingImage
                                    ? "Uploading image..."
                                    : isLoading
                                    ? isEditMode
                                        ? "Updating..."
                                        : "Creating..."
                                    : "Save"}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default AddSubadmin;
